import React, { useState, useEffect } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import axiosInstance from '../../../../utils/axiosInstance';
import './SaveRecurringCardModal.css';

function SaveRecurringCardModal({
  isOpen,
  onClose,
  onCardSaved,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const modalRef = React.useRef(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleContentClick = (e) => {
    e.stopPropagation();
  };

  const handleSaveCard = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!stripe || !elements) {
      setErrorMessage('Stripe has not loaded yet. Please wait...');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setErrorMessage('Payment element not found.');
      return;
    }

    try {
      setIsSaving(true);
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        setErrorMessage(error.message);
        setIsSaving(false);
        return;
      }

      const token = localStorage.getItem('token');
      await axiosInstance.post(
        '/users/add-card',  
        { paymentMethodId: paymentMethod.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (onCardSaved) onCardSaved();
      onClose();
    } catch (err) {
      console.error('Failed to save card:', err);
      setErrorMessage('Failed to save card. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div 
      className="save-recurring-card-modal-overlay" 
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div 
        className="save-recurring-card-modal-content" 
        onClick={handleContentClick}
        ref={modalRef}
        tabIndex="-1"
      >
        <button 
          className="close-button" 
          onClick={onClose}
          aria-label="Close modal"
        >
          ×
        </button>
        <h2 id="modal-title">Save a Card on File</h2>
        <p>
          To make your membership recurring, you need a valid payment method 
          on file. Enter your card details below; it will be used for 
          future recurring charges.
        </p>

        <form onSubmit={handleSaveCard} className="card-form">
          <div className="card-element-wrapper">
            <CardElement 
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#2c3e50',
                    '::placeholder': {
                      color: '#aab7c4'
                    }
                  },
                  invalid: {
                    color: '#e74c3c'
                  }
                }
              }}
            />
          </div>

          {errorMessage && (
            <div className="error-message" role="alert">
              {errorMessage}
            </div>
          )}

          <button
            className={`save-card-button ${isSaving ? 'loading' : ''}`}
            type="submit"
            disabled={isSaving || !stripe || !elements}
            aria-busy={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Card'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default SaveRecurringCardModal;
